import { AlertCircle, ChevronLeft, ChevronRight, Database, Pencil, Plus, RefreshCw, Trash2 } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

import {
  createBaserowRow,
  deleteBaserowRow,
  listBaserowApplications,
  listBaserowFields,
  listBaserowRows,
  listBaserowTables,
  updateBaserowRow,
  type BaserowApplication,
  type BaserowField,
  type BaserowRowsResponse,
  type BaserowTable,
} from "@/services/baserow";

export function DataViewPanel() {
  const [apps, setApps] = React.useState<BaserowApplication[]>([]);
  const [tables, setTables] = React.useState<BaserowTable[]>([]);
  const [fields, setFields] = React.useState<BaserowField[]>([]);
  const [rowsResponse, setRowsResponse] = React.useState<BaserowRowsResponse | null>(null);
  const [selectedAppId, setSelectedAppId] = React.useState<string>("");
  const [selectedTableId, setSelectedTableId] = React.useState<string>("");
  const [page, setPage] = React.useState(1);
  const [size, setSize] = React.useState(50);
  const [loadingApps, setLoadingApps] = React.useState(false);
  const [loadingTables, setLoadingTables] = React.useState(false);
  const [loadingRows, setLoadingRows] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingRow, setEditingRow] = React.useState<Record<string, unknown> | null>(null);
  const [formValues, setFormValues] = React.useState<Record<string, unknown>>({});
  const [submitting, setSubmitting] = React.useState(false);

  const loadApps = React.useCallback(async () => {
    setLoadingApps(true);
    setError(null);
    try {
      const data = await listBaserowApplications();
      setApps(data);
      if (data.length > 0 && !selectedAppId) {
        setSelectedAppId(String(data[0].id));
      }
    } catch (err) {
      setApps([]);
      setError(extractErrorMessage(err, "无法连接到 Baserow 数据服务，请确认服务已启动。"));
    } finally {
      setLoadingApps(false);
    }
  }, [selectedAppId]);

  const loadTables = React.useCallback(async (appId: string) => {
    if (!appId) return;
    setLoadingTables(true);
    setError(null);
    try {
      const data = await listBaserowTables(Number(appId));
      setTables(data);
      if (data.length > 0) {
        setSelectedTableId(String(data[0].id));
      } else {
        setSelectedTableId("");
        setFields([]);
        setRowsResponse(null);
      }
    } catch (err) {
      setTables([]);
      setError(extractErrorMessage(err, "加载数据库表失败。"));
    } finally {
      setLoadingTables(false);
    }
  }, []);

  const loadRows = React.useCallback(async (tableId: string, pageNum: number, pageSize: number) => {
    if (!tableId) return;
    setLoadingRows(true);
    setError(null);
    try {
      const [fieldsData, rowsData] = await Promise.all([
        listBaserowFields(Number(tableId)),
        listBaserowRows(Number(tableId), pageNum, pageSize),
      ]);
      setFields(fieldsData);
      setRowsResponse(rowsData);
    } catch (err) {
      setFields([]);
      setRowsResponse(null);
      setError(extractErrorMessage(err, "加载表数据失败。"));
    } finally {
      setLoadingRows(false);
    }
  }, []);

  React.useEffect(() => {
    void loadApps();
  }, [loadApps]);

  React.useEffect(() => {
    void loadTables(selectedAppId);
  }, [selectedAppId, loadTables]);

  React.useEffect(() => {
    setPage(1);
  }, [selectedTableId]);

  React.useEffect(() => {
    void loadRows(selectedTableId, page, size);
  }, [selectedTableId, page, size, loadRows]);

  const totalCount = rowsResponse?.count ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / size));
  const rows = rowsResponse?.results ?? [];

  const openCreate = () => {
    setEditingRow(null);
    setFormValues(
      Object.fromEntries(
        fields.map((field) => {
          const defaultValue = field.type === "boolean" ? false : "";
          return [field.name, defaultValue];
        }),
      ),
    );
    setDialogOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setEditingRow(row);
    setFormValues(
      Object.fromEntries(
        fields.map((field) => {
          const value = row[field.name];
          return [field.name, value === null || value === undefined ? "" : value];
        }),
      ),
    );
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!selectedTableId) return;
    setSubmitting(true);
    try {
      if (editingRow) {
        await updateBaserowRow(Number(selectedTableId), Number(editingRow.id), formValues);
      } else {
        await createBaserowRow(Number(selectedTableId), formValues);
      }
      setDialogOpen(false);
      await loadRows(selectedTableId, page, size);
    } catch (err) {
      setError(extractErrorMessage(err, editingRow ? "更新行失败。" : "新增行失败。"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (row: Record<string, unknown>) => {
    if (!selectedTableId || typeof row.id !== "number") return;
    if (!window.confirm("确定删除该行？此操作不可撤销。")) return;
    try {
      await deleteBaserowRow(Number(selectedTableId), row.id);
      await loadRows(selectedTableId, page, size);
    } catch (err) {
      setError(extractErrorMessage(err, "删除行失败。"));
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Database className="h-4 w-4" />
            数据视图（Baserow）
          </span>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={() => void loadApps()} disabled={loadingApps}>
              <RefreshCw className={cn("mr-1 h-3.5 w-3.5", loadingApps && "animate-spin")} />
              刷新
            </Button>
            <Button size="sm" onClick={openCreate} disabled={!selectedTableId}>
              <Plus className="mr-1 h-3.5 w-3.5" />
              新增行
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 overflow-hidden">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Baserow 服务暂不可用</p>
              <p className="text-xs opacity-90">{error}</p>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">应用</label>
            <select
              value={selectedAppId}
              onChange={(e) => setSelectedAppId(e.target.value)}
              disabled={loadingApps || apps.length === 0}
              className="h-8 rounded-md border bg-background px-2 text-xs"
            >
              {apps.length === 0 && <option value="">无可用应用</option>}
              {apps.map((app) => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">数据表</label>
            <select
              value={selectedTableId}
              onChange={(e) => setSelectedTableId(e.target.value)}
              disabled={loadingTables || tables.length === 0}
              className="h-8 min-w-[8rem] rounded-md border bg-background px-2 text-xs"
            >
              {tables.length === 0 && <option value="">无可用表</option>}
              {tables.map((table) => (
                <option key={table.id} value={table.id}>
                  {table.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground">每页</label>
            <Input
              type="number"
              min={10}
              max={200}
              value={size}
              onChange={(e) => setSize(Math.max(10, Math.min(200, Number(e.target.value) || 50)))}
              className="h-8 w-16 text-xs"
            />
          </div>
        </div>

        <div className="flex flex-1 flex-col overflow-hidden rounded-md border">
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-background">
                <TableRow>
                  {fields.length === 0 ? (
                    <TableHead>暂无字段</TableHead>
                  ) : (
                    <>
                      {fields.map((field) => (
                        <TableHead key={field.id}>{field.name}</TableHead>
                      ))}
                      <TableHead className="w-24">操作</TableHead>
                    </>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingRows ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, fields.length + 1)} className="text-center text-muted-foreground">
                      加载中...
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={Math.max(1, fields.length + 1)} className="text-center text-muted-foreground">
                      暂无数据
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow key={idx}>
                      {fields.map((field) => (
                        <TableCell key={field.id} className="max-w-xs truncate text-xs">
                          {formatCellValue(row[field.name])}
                        </TableCell>
                      ))}
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(row)}>
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-destructive"
                            onClick={() => void handleDelete(row)}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between border-t px-4 py-2 text-xs">
            <span className="text-muted-foreground">
              共 {totalCount} 条，第 {page} / {totalPages} 页
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1 || loadingRows}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || loadingRows}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[80vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{editingRow ? "编辑行" : "新增行"}</DialogTitle>
            <DialogDescription>在「{tables.find((t) => String(t.id) === selectedTableId)?.name}」表中{editingRow ? "更新" : "创建"}数据。</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            {fields.map((field) => (
              <div key={field.id} className="grid gap-2">
                <Label className="text-xs">{field.name}</Label>
                {field.type === "boolean" ? (
                  <Switch
                    checked={Boolean(formValues[field.name])}
                    onCheckedChange={(checked) =>
                      setFormValues((prev) => ({ ...prev, [field.name]: checked }))
                    }
                  />
                ) : field.type === "number" || field.type === "rating" ? (
                  <Input
                    type="number"
                    value={String(formValues[field.name] ?? "")}
                    onChange={(e) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: e.target.value === "" ? "" : Number(e.target.value),
                      }))
                    }
                    className="h-8 text-xs"
                  />
                ) : (
                  <Input
                    value={String(formValues[field.name] ?? "")}
                    onChange={(e) =>
                      setFormValues((prev) => ({ ...prev, [field.name]: e.target.value }))
                    }
                    className="h-8 text-xs"
                  />
                )}
              </div>
            ))}
            {fields.length === 0 && (
              <p className="text-sm text-muted-foreground">当前表无字段，无法{editingRow ? "编辑" : "新增"}。</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              取消
            </Button>
            <Button onClick={() => void handleSubmit()} disabled={submitting || fields.length === 0}>
              {submitting ? "保存中..." : "保存"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (typeof value === "boolean") return value ? "是" : "否";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message || fallback;
  }
  return fallback;
}
